<div class="feature-usage">

  <div class="tab-header">
    <div class="dl-field feature-usage-period">
      <label class="dl-field__label" for="usagePeriod">Month</label>
      <select id="usagePeriod" class="dl-select" [(ngModel)]="period" (change)="onPeriodChange()" [disabled]="refreshing">
        <option *ngFor="let option of periodOptions" [value]="option">{{ option }}</option>
      </select>
    </div>
    <p class="dl-page__subtitle" *ngIf="generatedAt">
      Read live at {{ generatedAt | date:'short' }} — these counters are never cached.
    </p>
  </div>

  <div *ngIf="loading" class="dl-loading">
    <div class="dl-spinner"></div>
    <p>Loading feature usage...</p>
  </div>

  <div *ngIf="error" class="dl-banner dl-banner--error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
    <button (click)="load()">
      <mat-icon>refresh</mat-icon>
      Try Again
    </button>
  </div>

  <div *ngIf="!loading && !error" [class.dl-is-refreshing]="refreshing">

    <div class="dl-stat-grid">
      <div class="dl-stat">
        <div class="dl-stat__value">{{ totals.voiceMinutes }}</div>
        <div class="dl-stat__label">Voice minutes transcribed</div>
        <div class="dl-stat__sub">{{ totals.voiceTenants }} tenants spending</div>
      </div>
      <div class="dl-stat">
        <div class="dl-stat__value">{{ totals.botRequests }}</div>
        <div class="dl-stat__label">Assistant requests</div>
        <div class="dl-stat__sub">{{ totals.botTenants }} tenants spending</div>
      </div>
      <div class="dl-stat">
        <div class="dl-stat__value">{{ totals.tokens | number }}</div>
        <div class="dl-stat__label" matTooltip="Prompt and completion tokens together — what the provider invoices">
          LLM tokens
        </div>
        <div class="dl-stat__sub">across every feature</div>
      </div>
      <div class="dl-stat" [class.dl-stat--bad]="totals.nearLimit > 0">
        <div class="dl-stat__value">{{ totals.nearLimit }}</div>
        <div class="dl-stat__label">At 80% of allowance or over</div>
        <div class="dl-stat__sub">tenants worth a phone call</div>
      </div>
    </div>

    <div class="charts">
      <div class="dl-card usage-chart-card">
        <h3>Share of monthly allowance used</h3>
        <p class="usage-chart-sub">
          Percent rather than raw units, because one axis cannot be shared between minutes and
          requests. Only tenants that spent something this month appear here.
        </p>
        <div class="chart-host">
          <ngx-charts-bar-horizontal-stacked
            [results]="chartData"
            [scheme]="usageScheme"
            [xAxis]="true"
            [yAxis]="true"
            [legend]="true"
            legendTitle=""
            [showDataLabel]="true"
            [barPadding]="6">
          </ngx-charts-bar-horizontal-stacked>
        </div>
        <p class="usage-chart-note" *ngIf="chartTailNote">{{ chartTailNote }}</p>
        <p class="usage-chart-note" *ngIf="chartData.length === 0">Nobody spent anything in {{ period }}.</p>
      </div>
    </div>

    <div class="dl-card">
      <div class="dl-card__header">
        <h3>Spend by tenant</h3>
        <span class="dl-card__subtitle">{{ filteredRows.length }} of {{ rows.length }}</span>
      </div>

      <!-- Filtering here is pure array work against data already in memory: no request, so no
           debounce and nothing to disable while it runs. Changing the month does reload. -->
      <div class="dl-filters usage-filters">
        <div class="dl-field">
          <label class="dl-field__label" for="featureSearch">Search</label>
          <input id="featureSearch" type="text" [(ngModel)]="searchTerm" (input)="applyFilters()"
                 placeholder="Tenant or feature" class="dl-input" />
        </div>
        <div class="dl-field">
          <label class="dl-field__label" for="featureSelect">Feature</label>
          <select id="featureSelect" class="dl-select" [(ngModel)]="featureFilter" (change)="applyFilters()">
            <option value="">All features</option>
            <option *ngFor="let option of featureOptions" [value]="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="dl-field feature-usage-check">
          <label class="dl-switch">
            <input type="checkbox" [(ngModel)]="overQuotaOnly" (change)="applyFilters()" />
            <span class="dl-switch__track"></span>
            <span class="dl-switch__label">At 80% or over</span>
          </label>
        </div>
        <div class="dl-field usage-filters__action">
          <button type="button" class="dl-btn dl-btn--ghost" (click)="clearFilters()">Clear</button>
        </div>
      </div>

      <div class="table-scroll">
        <table class="dl-table dl-table--cards" dlCardTable>
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Feature</th>
              <th>Used</th>
              <th>Allowance</th>
              <th>Share used</th>
              <th matTooltip="Prompt and completion tokens — what the provider invoices">Tokens</th>
              <th>Uses</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of filteredRows; trackBy: trackByRow">
              <td>
                <a [routerLink]="['/partners', row.tenantId]" class="tenant-link">{{ row.tenantName }}</a>
              </td>
              <td>{{ row.featureLabel }}</td>
              <td>{{ row.usedLabel }}</td>
              <td>
                {{ row.limitLabel }}
                <span class="free-plan" *ngIf="row.onFreePlan"
                      matTooltip="No figure set for this tenant, so it takes the configured free-plan default.">
                  free plan
                </span>
              </td>
              <td>
                <div class="quota-bar" [ngClass]="'quota-bar--' + row.severity">
                  <div class="quota-bar-fill" [style.width.%]="row.percentUsed > 100 ? 100 : row.percentUsed"></div>
                </div>
                <span class="quota-percent">{{ row.percentUsed }}%</span>
              </td>
              <td [class.muted]="row.tokens === 0">{{ row.tokens | number }}</td>
              <td>{{ row.events }}</td>
              <td>
                <span class="flag-state" [class.flag-state--off]="!row.enabled">
                  {{ row.enabled ? 'On' : 'Off' }}
                </span>
              </td>
            </tr>
            <tr *ngIf="filteredRows.length === 0">
              <td colspan="8" class="no-data">No tenants match the current filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
